/**
 * Current-user profile, password, Unipile/LinkedIn linkage, API keys, and operator-scoped actions.
 *
 * Where: `Profile`, `ApiKeyManagement`, `LinkedInConnection`, admin user tooling.
 * Uses: `apiClient`; overlaps conceptually with `teamApi` for some admin operations.
 * Contract: profile — GET `/user/profile/`, PATCH `/user/profile/update/`, POST multipart `/user/profile/image/` → typically `response.data.output`; LinkedIn — GET `/user/linkedin/status/`, POST `/unipile/auth-url/`; disconnect — POST `/users/:userUuid/disconnect-unipile/`; jobs — POST `/user/sync-messages/`, `/user/withdraw-pending-invites/`, GET `/user/sync-jobs/`; API keys — GET/POST/DELETE `/user/api-key/…`. Several methods return `response.data.output`; sync-job helpers may return full `response.data` — see each wrapper.
 * Behavior: optional `user_uuid` query on admin-style job calls.
 */

import { apiClient } from './client';
import type { User } from '@/types/auth';

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  password?: string;
  unipile_id?: string;
}

export interface LinkedInConnectionStatus {
  is_connected: boolean;
  has_account: boolean;
  premium_features?: string[];
}

export interface ApiKeyInfo {
  type: string;
  key: string;
  created_at: string;
  name: string;
  prefix: string;
  scopes: string[];
  last_used_at: string | null;
  raw_key?: string;
}

export interface GenerateApiKeyRequest {
  name?: string;
  scopes?: string[];
}

export const userApi = {
  /**
   * Get current user profile
   */
  getProfile: async (addStatistics?: boolean): Promise<User> => {
    const params: Record<string, any> = {};
    if (addStatistics) {
      params['add_statistics'] = 'true';
    }
    const response = await apiClient.get<{ output: User }>('/user/profile/', { params });
    return response.data.output;
  },

  /**
   * Update current user profile
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<User> => {
    const response = await apiClient.patch<{ output: User }>('/user/profile/update/', data);
    return response.data.output;
  },

  /**
   * Upload profile image
   */
  uploadProfileImage: async (file: File): Promise<User> => {
    const formData = new FormData();
    formData.append('profile_image', file);
    
    const response = await apiClient.post<{ output: User }>('/user/profile/image/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.output;
  },

  /**
   * Get LinkedIn connection status
   */
  getLinkedInStatus: async (userUuid?: string, forceFromSource?: boolean): Promise<LinkedInConnectionStatus> => {
    const params: Record<string, any> = {};
    if (userUuid) {
      params['user_uuid'] = userUuid;
    }
    if (forceFromSource) {
      params['force_from_source'] = true;
    }
    const response = await apiClient.get<{ output: LinkedInConnectionStatus }>('/user/linkedin/status/', {
      params,
    });
    return response.data.output;
  },

  /**
   * Create LinkedIn auth URL
   */
  createLinkedInAuthUrl: async (): Promise<{ url: string }> => {
    const response = await apiClient.post<{ output: string }>('/unipile/auth-url/');
    return { url: response.data.output };
  },

  /**
   * Disconnect Unipile account for a user
   */
  disconnectUnipileAccount: async (userUuid: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ output: { message: string } }>(
      `/users/${userUuid}/disconnect-unipile/`
    );
    return response.data.output;
  },

  /**
   * Create a sync messages job for a user
   */
  createSyncMessagesJob: async (
    userUuid?: string,
    fromDate?: string,
    toDate?: string
  ): Promise<{ output: any }> => {
    const params: Record<string, any> = {};
    if (userUuid) {
      params['user_uuid'] = userUuid;
    }
    const response = await apiClient.post<{ output: any }>(
      '/user/sync-messages/',
      {
        from_date: fromDate,
        to_date: toDate,
      },
      { params }
    );
    return response.data;
  },

  /**
   * Create a job to recalculate AI chat tags for a user.
   */
  createRecalculateUserChatAiTagsJob: async (
    userUuid?: string,
    fromDate?: string,
    toDate?: string
  ): Promise<{ output: any }> => {
    const params: Record<string, any> = {};
    if (userUuid) {
      params['user_uuid'] = userUuid;
    }
    const response = await apiClient.post<{ output: any }>(
      '/user/recalculate-ai-tags/',
      {
        from_date: fromDate,
        to_date: toDate,
      },
      { params }
    );
    return response.data;
  },

  /**
   * Create a withdraw pending invites job for a user
   */
  createWithdrawPendingInvitesJob: async (
    userUuid?: string
  ): Promise<{ output: any }> => {
    const params: Record<string, any> = {};
    if (userUuid) {
      params['user_uuid'] = userUuid;
    }
    const response = await apiClient.post<{ output: any }>(
      '/user/withdraw-pending-invites/',
      {},
      { params }
    );
    return response.data;
  },

  /**
   * Get sync jobs for a user
   */
  getUserSyncJobs: async (
    userUuid?: string,
    params?: {
      status?: string;
      page_token?: string;
      max_results?: number;
    }
  ): Promise<{ output: { items: any[]; next_page_token?: string; previous_page_token?: string; total_results?: number } }> => {
    const queryParams: Record<string, any> = { ...params };
    if (userUuid) {
      queryParams['user_uuid'] = userUuid;
    }
    const response = await apiClient.get<{ output: { items: any[]; next_page_token?: string; previous_page_token?: string; total_results?: number } }>(
      '/user/sync-jobs/',
      { params: queryParams }
    );
    return response.data;
  },

  /**
   * Get current user's API key metadata
   */
  getApiKey: async (): Promise<ApiKeyInfo | null> => {
    const response = await apiClient.get<{ output: ApiKeyInfo | null }>('/user/api-key/');
    return response.data.output;
  },

  /**
   * Generate a new API key (revokes existing one)
   */
  generateApiKey: async (data?: GenerateApiKeyRequest): Promise<ApiKeyInfo> => {
    const response = await apiClient.post<{ output: ApiKeyInfo }>('/user/api-key/generate/', data || {});
    return response.data.output;
  },

  /**
   * Revoke the current API key
   */
  revokeApiKey: async (): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ output: { message: string } }>('/user/api-key/revoke/');
    return response.data.output;
  },
}; 